/**
 * @file spendCap.js
 * @description Client side helpers for the agency spend cap. UX ONLY: the cap is
 *   enforced server side (edge functions + invoice trigger, see
 *   supabase/functions/_shared/billing/spendCap.ts). Everything here either reads the
 *   server's answer or turns a blocked response into something the UI can show.
 *
 *   The server blocks a purchase with:
 *     - HTTP 409 { error: "spend_cap_exceeded", cap_cents, spent_cents, price_cents }
 *       from the Stripe edge functions, or
 *     - a Postgres error with message "spend_cap_exceeded" and a JSON `details`
 *       from the in-app RPCs (the invoice trigger).
 *
 * @module lib/spendCap
 */
import { supabase } from "./supabase";
import { functionErrorBody, functionErrorStatus } from "./functionErrorMessage";

export const SPEND_CAP_ERROR = "spend_cap_exceeded";

function toInfo(o) {
  if (!o || typeof o !== "object") return null;
  const num = (v) => (v == null || v === "" || !Number.isFinite(Number(v)) ? null : Number(v));
  return {
    capCents:   num(o.cap_cents),
    spentCents: num(o.spent_cents) ?? 0,
    priceCents: num(o.price_cents) ?? 0,
    periodEnd:  o.period_end || null,
  };
}

/**
 * Pull a spend cap block out of any server answer. Accepts an edge function JSON
 * body, a PostgREST/Postgres error ({ message, details }), or a thrown value.
 * Returns { capCents, spentCents, priceCents, periodEnd } or null.
 */
export function spendCapInfoFrom(value) {
  if (!value || typeof value !== "object") return null;
  if (value.error === SPEND_CAP_ERROR) return toInfo(value);
  if (value.message === SPEND_CAP_ERROR) {
    let d = value.details;
    if (typeof d === "string") { try { d = JSON.parse(d); } catch { d = null; } }
    return toInfo(d || {});
  }
  return null;
}

/**
 * supabase.functions.invoke() hands a non-2xx answer back as an error whose body is on error.context
 * (read by functionErrorMessage.js). Read the 409 body so the cap details survive. Returns spendCapInfo or null.
 */
export async function spendCapInfoFromInvokeError(error) {
  if (functionErrorStatus(error) !== 409) return null;
  const body = await functionErrorBody(error);
  return body ? spendCapInfoFrom(body) : null;
}

/** Whole dollars when exact, otherwise two decimals. */
export function fmtCents(cents) {
  const n = (Number(cents) || 0) / 100;
  return n.toLocaleString("en-US", {
    style: "currency", currency: "USD",
    minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Cap, spend and window for the agency's current billing period.
 * @returns {Promise<{capCents:number|null, spentCents:number, periodStart:string|null, periodEnd:string|null}|null>}
 */
export async function loadSpendStatus(agencyId) {
  if (!agencyId) return null;
  const { data, error } = await supabase.rpc("agency_period_spend", { p_agency_id: agencyId });
  if (error || !data) return null;
  return {
    capCents:    data.cap_cents ?? null,
    spentCents:  Number(data.spent_cents) || 0,
    periodStart: data.period_start || null,
    periodEnd:   data.period_end || null,
  };
}
